import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import { Box, HStack, Text, Textarea } from '@chakra-ui/react';
import Constants from '../../../Constants';
import { convertToCamelCase } from '../../../helpers';
import GeneratedDocuments from '../GeneratedDocuments';
import PurchaseOrderDocuments from '../PurchaseOrderDocuments';
import IssuesXcodes from '../IssuesXcodes/IssuesXcodes';
import { Field } from '@/components/ui/field';

const CommentsAttachmentsTab = ({
  editing,
  purchaseOrder,
  purchaseOrderCameled,
  currentOrderCameled,
  grinder,
  user,
  poDocTypes,
  token,
  error,
  dispatch,
  issues_xcode,
  allIssuesXcodes,
  internalPoLabel,
  isGeneratingDocument,
  setIsGeneratingDocument,
  setEmailModalIsOpen,
  onChangeField,
  onIssueXcodeChange,
  onIssueXcodeAdd,
  onIssueXcodeDelete,
}) => {
  const [internalComment, setInternalComment] = useState(purchaseOrderCameled?.internalComments);
  const [externalComment, setExternalComment] = useState(purchaseOrderCameled?.externalComments);

  const handleInternalCommentChange = useCallback(e => setInternalComment(e.target.value), []);

  const handleExternalCommentChange = useCallback(e => setExternalComment(e.target.value), []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChangeField('internal_comments', internalComment);
    }, 300);
    return () => clearTimeout(timeout);
  }, [internalComment]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChangeField('external_comments', externalComment);
    }, 300);
    return () => clearTimeout(timeout);
  }, [externalComment]);

  return (
    <>
      <Text fontWeight="bold" mb="1em">
        Comments
      </Text>
      <HStack spacing="42px" width="100%">
        <Box width="100%">
          <Field label="Internal Comments" htmlFor="internalComments">
            <Textarea
              backgroundColor="white"
              className="form-control"
              disabled={!editing}
              id="internalComments"
              name="internalComments"
              value={internalComment}
              placeholder="Add a comment here..."
              onChange={handleInternalCommentChange}
              rows="4"
            />
          </Field>
        </Box>
        <Box width="100%">
          <Field label="External Comments" htmlFor="externalComments">
            <Textarea
              backgroundColor="white"
              className="form-control"
              disabled={!editing}
              id="externalComments"
              name="externalComments"
              value={externalComment}
              placeholder="Add a comment here..."
              onChange={handleExternalCommentChange}
              rows="4"
            />
          </Field>
        </Box>
      </HStack>

      <Text fontWeight="bold" my="1.5em">
        Load Notes
      </Text>
      <IssuesXcodes
        isInEditMode={editing}
        issuesXcodesOption={issues_xcode}
        allIssuesXcodesOption={allIssuesXcodes}
        onChange={onIssueXcodeChange}
        onAdd={onIssueXcodeAdd}
        onDelete={onIssueXcodeDelete}
        issuesXcodes={get(purchaseOrder, 'issues_xcodes', [])}
      />

      <Text fontWeight="bold" my="1.5em">
        Documents
      </Text>
      <PurchaseOrderDocuments
        token={token}
        error={error}
        {...convertToCamelCase({
          autoSave: false,
          disabled: !editing,
          dispatch,
          documents: purchaseOrderCameled.documents.filter(
            doc => !Constants.GENERATED_DOCUMENT_TYPES_LIST.includes(doc.docType)
          ),
          poDocTypes,
          user,
        })}
        stateForm={{ form: purchaseOrder }}
        currentOrder={purchaseOrder}
      />

      <Text fontWeight="bold" my="1.5em">
        Generated Documents
      </Text>
      <GeneratedDocuments
        form={purchaseOrderCameled}
        currentOrder={currentOrderCameled}
        isInEditMode={editing}
        internalPoLabel={internalPoLabel}
        user={user}
        grinderIsChinese={(grinder?.destinationCountry?.toLowerCase() || '') === Constants.COUNTRIES.CHINA}
        setIsGeneratingDocument={setIsGeneratingDocument}
        isGeneratingDocument={isGeneratingDocument}
        setEmailModalIsOpen={setEmailModalIsOpen}
      />
    </>
  );
};
CommentsAttachmentsTab.propTypes = {
  editing: PropTypes.bool.isRequired,
  purchaseOrder: PropTypes.shape({}),
  purchaseOrderCameled: PropTypes.shape({
    internalComments: PropTypes.string,
    externalComments: PropTypes.string,
    documents: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  currentOrderCameled: PropTypes.shape({}),
  grinder: PropTypes.shape({
    destinationCountry: PropTypes.string,
  }),
  user: PropTypes.shape({}),
  poDocTypes: PropTypes.arrayOf(PropTypes.shape({})),
  token: PropTypes.string,
  error: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  dispatch: PropTypes.func,
  issues_xcode: PropTypes.arrayOf(PropTypes.shape({})),
  allIssuesXcodes: PropTypes.arrayOf(PropTypes.shape({})),
  internalPoLabel: PropTypes.string,
  isGeneratingDocument: PropTypes.bool,
  setIsGeneratingDocument: PropTypes.func,
  setEmailModalIsOpen: PropTypes.func,
  onChangeField: PropTypes.func,
  onIssueXcodeChange: PropTypes.func,
  onIssueXcodeAdd: PropTypes.func,
  onIssueXcodeDelete: PropTypes.func,
};

export default React.memo(CommentsAttachmentsTab);
